import { DeviceType } from '../../../NonReact/constants/DeviceTypes'
import {
    DEVICE_MODAL_CAPABILITY_BROADWORKS_ID_OR_UPN_WEBEX_USER_ID,
    DEVICE_MODAL_CAPABILITY_CISCO_COMMON_IDENTITY,
    DEVICE_MODAL_CAPABILITY_TELSYS_MONITOR_PASSWORD,
    DEVICE_MODAL_CAPABILITY_DISABLE_REALTIME_QUEUE_MONITORING,
    DEVICE_MODAL_CAPABILITY_HUNT_GROUP_AGENT_MEMBERSHIP,
    DEVICE_MODAL_CAPABILITY_HUNT_GROUP_DEVICE_MEMBERSHIP,
    DEVICE_MODAL_CAPABILITY_HUNT_GROUP_DNIS_ASSOCIATIONS,
    DEVICE_MODAL_CAPABILITY_INT_DIALLING_ADDRESS,
    DEVICE_MODAL_CAPABILITY_MS_TEAMS_USER_ID,
    DEVICE_MODAL_CAPABILITY_OVERRIDE_MONITORING,
    DEVICE_MODAL_CAPABILITY_OWNING_DEPARTMENT_FULL_PATH,
    DEVICE_MODAL_CAPABILITY_OWNING_GROUP_ID,
    DEVICE_MODAL_CAPABILITY_PRIMARY_EMAIL_ADDRESS,
    DEVICE_MODAL_CAPABILITY_UPDATE_EXISTING_DUPLICATE_ITEMS,
    DeviceModalCapabilityFlags,
    DeviceModalCapabilityKey
} from './DeviceModalCapabilityKeys'

/**
 * Primary {@code CTelephonyModelConst.iDEVICE_TYPE_XXX} values for each Confluence matrix row.
 * {@link DeviceModalMatrixRowId.CALL_QUEUE} duplicates {@code GROUP_ACD} (43) with different flags
 * (MS Teams {@code Call Queue} label); see {@code CWSConstants.ConvertDeviceTypeStringToInt}.
 */
const Y = true
const N = false

const caps = (p: Record<DeviceModalCapabilityKey, boolean>): DeviceModalCapabilityFlags => Object.freeze({ ...p })

/** Standard non-group endpoint row from Confluence (hunt membership columns empty). */
const STANDARD_NON_GROUP_FLAGS: DeviceModalCapabilityFlags = caps({
    [DEVICE_MODAL_CAPABILITY_INT_DIALLING_ADDRESS]: Y,
    [DEVICE_MODAL_CAPABILITY_OVERRIDE_MONITORING]: Y,
    [DEVICE_MODAL_CAPABILITY_BROADWORKS_ID_OR_UPN_WEBEX_USER_ID]: Y,
    [DEVICE_MODAL_CAPABILITY_MS_TEAMS_USER_ID]: Y,
    [DEVICE_MODAL_CAPABILITY_CISCO_COMMON_IDENTITY]: Y,
    [DEVICE_MODAL_CAPABILITY_PRIMARY_EMAIL_ADDRESS]: N,
    [DEVICE_MODAL_CAPABILITY_DISABLE_REALTIME_QUEUE_MONITORING]: N,
    [DEVICE_MODAL_CAPABILITY_OWNING_GROUP_ID]: Y,
    [DEVICE_MODAL_CAPABILITY_OWNING_DEPARTMENT_FULL_PATH]: Y,
    [DEVICE_MODAL_CAPABILITY_UPDATE_EXISTING_DUPLICATE_ITEMS]: Y,
    [DEVICE_MODAL_CAPABILITY_HUNT_GROUP_DEVICE_MEMBERSHIP]: N,
    [DEVICE_MODAL_CAPABILITY_HUNT_GROUP_AGENT_MEMBERSHIP]: N,
    [DEVICE_MODAL_CAPABILITY_HUNT_GROUP_DNIS_ASSOCIATIONS]: N,
    [DEVICE_MODAL_CAPABILITY_TELSYS_MONITOR_PASSWORD]: N
})

/**
 * Capability flags used when a {@code DeviceTypeID} is not present in the APM matrix (defensive UI fallback).
 */
export const DEVICE_MODAL_CAPABILITY_FLAGS_UNMAPPED_DEVICE_TYPE: DeviceModalCapabilityFlags = STANDARD_NON_GROUP_FLAGS

const EXTENSION_ENDPOINT_FLAGS: DeviceModalCapabilityFlags = caps({
    ...STANDARD_NON_GROUP_FLAGS,
    [DEVICE_MODAL_CAPABILITY_PRIMARY_EMAIL_ADDRESS]: Y
})

/** Group row: hunt device + agent; DNIS empty (Confluence matrix). */
const STANDARD_GROUP_FLAGS_NO_DNIS: DeviceModalCapabilityFlags = caps({
    [DEVICE_MODAL_CAPABILITY_INT_DIALLING_ADDRESS]: Y,
    [DEVICE_MODAL_CAPABILITY_OVERRIDE_MONITORING]: Y,
    [DEVICE_MODAL_CAPABILITY_BROADWORKS_ID_OR_UPN_WEBEX_USER_ID]: Y,
    [DEVICE_MODAL_CAPABILITY_MS_TEAMS_USER_ID]: Y,
    [DEVICE_MODAL_CAPABILITY_CISCO_COMMON_IDENTITY]: Y,
    [DEVICE_MODAL_CAPABILITY_PRIMARY_EMAIL_ADDRESS]: N,
    [DEVICE_MODAL_CAPABILITY_DISABLE_REALTIME_QUEUE_MONITORING]: N,
    [DEVICE_MODAL_CAPABILITY_OWNING_GROUP_ID]: Y,
    [DEVICE_MODAL_CAPABILITY_OWNING_DEPARTMENT_FULL_PATH]: Y,
    [DEVICE_MODAL_CAPABILITY_UPDATE_EXISTING_DUPLICATE_ITEMS]: Y,
    [DEVICE_MODAL_CAPABILITY_HUNT_GROUP_DEVICE_MEMBERSHIP]: Y,
    [DEVICE_MODAL_CAPABILITY_HUNT_GROUP_AGENT_MEMBERSHIP]: Y,
    [DEVICE_MODAL_CAPABILITY_HUNT_GROUP_DNIS_ASSOCIATIONS]: N,
    [DEVICE_MODAL_CAPABILITY_TELSYS_MONITOR_PASSWORD]: N
})

/** Group ACD (type 43): hunt membership and DNIS; {@link CALL_QUEUE_FLAGS} adds MS Teams Call Queue-only flags. */
const GROUP_ACD_FLAGS: DeviceModalCapabilityFlags = caps({
    ...STANDARD_GROUP_FLAGS_NO_DNIS,
    [DEVICE_MODAL_CAPABILITY_HUNT_GROUP_DNIS_ASSOCIATIONS]: Y
})

const CALL_QUEUE_FLAGS: DeviceModalCapabilityFlags = caps({
    [DEVICE_MODAL_CAPABILITY_INT_DIALLING_ADDRESS]: Y,
    [DEVICE_MODAL_CAPABILITY_OVERRIDE_MONITORING]: N,
    [DEVICE_MODAL_CAPABILITY_BROADWORKS_ID_OR_UPN_WEBEX_USER_ID]: Y,
    [DEVICE_MODAL_CAPABILITY_MS_TEAMS_USER_ID]: Y,
    [DEVICE_MODAL_CAPABILITY_CISCO_COMMON_IDENTITY]: Y,
    [DEVICE_MODAL_CAPABILITY_PRIMARY_EMAIL_ADDRESS]: N,
    [DEVICE_MODAL_CAPABILITY_DISABLE_REALTIME_QUEUE_MONITORING]: Y,
    [DEVICE_MODAL_CAPABILITY_OWNING_GROUP_ID]: Y,
    [DEVICE_MODAL_CAPABILITY_OWNING_DEPARTMENT_FULL_PATH]: Y,
    [DEVICE_MODAL_CAPABILITY_UPDATE_EXISTING_DUPLICATE_ITEMS]: Y,
    [DEVICE_MODAL_CAPABILITY_HUNT_GROUP_DEVICE_MEMBERSHIP]: Y,
    [DEVICE_MODAL_CAPABILITY_HUNT_GROUP_AGENT_MEMBERSHIP]: Y,
    [DEVICE_MODAL_CAPABILITY_HUNT_GROUP_DNIS_ASSOCIATIONS]: Y,
    [DEVICE_MODAL_CAPABILITY_TELSYS_MONITOR_PASSWORD]: N
})

/**
 * Confluence {@code Device Type} row identifiers (stable keys for the matrix).
 */
export const DeviceModalMatrixRowId = {
    TRUNK: 'TRUNK',
    TRUNK_GATEWAY: 'TRUNK_GATEWAY',
    EXTENSION_ENDPOINT: 'EXTENSION_ENDPOINT',
    EXTENSION_TRUNKING: 'EXTENSION_TRUNKING',
    EXTENSION_HOSPITALITY: 'EXTENSION_HOSPITALITY',
    PARK_DEVICE: 'PARK_DEVICE',
    VOICEMAIL_APPLICATION: 'VOICEMAIL_APPLICATION',
    QUEUEING_ANNOUNCEMENT_HUNT_GROUP: 'QUEUEING_ANNOUNCEMENT_HUNT_GROUP',
    QUEUEING_ANNOUNCEMENT_GENERAL: 'QUEUEING_ANNOUNCEMENT_GENERAL',
    AUTO_ATTENDANT: 'AUTO_ATTENDANT',
    MUSIC_ON_HOLD: 'MUSIC_ON_HOLD',
    CONFERENCE: 'CONFERENCE',
    PILOT_ALTERNATIVE: 'PILOT_ALTERNATIVE',
    DNIS_ENTRY: 'DNIS_ENTRY',
    DEPARTMENT: 'DEPARTMENT',
    SUPER_GROUP: 'SUPER_GROUP',
    GROUP_COLLECTIVE: 'GROUP_COLLECTIVE',
    GROUP_LINEAR: 'GROUP_LINEAR',
    GROUP_CYCLIC: 'GROUP_CYCLIC',
    GROUP_ACD: 'GROUP_ACD',
    CALL_QUEUE: 'CALL_QUEUE',
    GROUP_TRUNK: 'GROUP_TRUNK',
    GROUP_VOICEMAIL: 'GROUP_VOICEMAIL',
    GROUP_PAGE: 'GROUP_PAGE'
} as const

export type DeviceModalMatrixRowId = (typeof DeviceModalMatrixRowId)[keyof typeof DeviceModalMatrixRowId]

export type DeviceModalMatrixRowDefinition = Readonly<{
    rowId: DeviceModalMatrixRowId
    /** Human title from Confluence (for diagnostics and future i18n). */
    confluenceTitle: string
    /** Primary {@code CTelephonyModelConst} device type id(s) for this row. */
    primaryDeviceTypeIds: readonly number[]
    capabilities: DeviceModalCapabilityFlags
}>

/**
 * Full matrix keyed by {@link DeviceModalMatrixRowId}, transcribed from Confluence APM
 * {@code Device Modal} (page {@code 4501209091}), version {@code 5} at import time.
 */
export const DEVICE_MODAL_CAPABILITY_MATRIX: Readonly<Record<DeviceModalMatrixRowId, DeviceModalMatrixRowDefinition>> =
    Object.freeze({
        [DeviceModalMatrixRowId.TRUNK]: {
            rowId: DeviceModalMatrixRowId.TRUNK,
            confluenceTitle: 'Trunk',
            primaryDeviceTypeIds: [DeviceType.Trunk],
            capabilities: STANDARD_NON_GROUP_FLAGS
        },
        [DeviceModalMatrixRowId.TRUNK_GATEWAY]: {
            rowId: DeviceModalMatrixRowId.TRUNK_GATEWAY,
            confluenceTitle: 'Trunk Gateway',
            primaryDeviceTypeIds: [DeviceType.TrunkGateway],
            capabilities: STANDARD_NON_GROUP_FLAGS
        },
        [DeviceModalMatrixRowId.EXTENSION_ENDPOINT]: {
            rowId: DeviceModalMatrixRowId.EXTENSION_ENDPOINT,
            confluenceTitle: 'Extension Endpoint',
            primaryDeviceTypeIds: [DeviceType.ExtensionOrEndpoint],
            capabilities: EXTENSION_ENDPOINT_FLAGS
        },
        [DeviceModalMatrixRowId.EXTENSION_TRUNKING]: {
            rowId: DeviceModalMatrixRowId.EXTENSION_TRUNKING,
            confluenceTitle: 'Extension (Trunking)',
            primaryDeviceTypeIds: [DeviceType.ExtensionOrEndpointTrunking],
            capabilities: STANDARD_NON_GROUP_FLAGS
        },
        [DeviceModalMatrixRowId.EXTENSION_HOSPITALITY]: {
            rowId: DeviceModalMatrixRowId.EXTENSION_HOSPITALITY,
            confluenceTitle: 'Extension (Hospitality)',
            primaryDeviceTypeIds: [DeviceType.ExtensionOrEndpointHospitality],
            capabilities: STANDARD_NON_GROUP_FLAGS
        },
        [DeviceModalMatrixRowId.PARK_DEVICE]: {
            rowId: DeviceModalMatrixRowId.PARK_DEVICE,
            confluenceTitle: 'Park Device',
            primaryDeviceTypeIds: [DeviceType.Park],
            capabilities: STANDARD_NON_GROUP_FLAGS
        },
        [DeviceModalMatrixRowId.VOICEMAIL_APPLICATION]: {
            rowId: DeviceModalMatrixRowId.VOICEMAIL_APPLICATION,
            confluenceTitle: 'VoiceMail Application',
            primaryDeviceTypeIds: [DeviceType.VoicemailApplication],
            capabilities: STANDARD_NON_GROUP_FLAGS
        },
        [DeviceModalMatrixRowId.QUEUEING_ANNOUNCEMENT_HUNT_GROUP]: {
            rowId: DeviceModalMatrixRowId.QUEUEING_ANNOUNCEMENT_HUNT_GROUP,
            confluenceTitle: 'Queueing Announcement (Hunt Group)',
            primaryDeviceTypeIds: [DeviceType.VoicemailAnnouncementHuntGroup],
            capabilities: STANDARD_NON_GROUP_FLAGS
        },
        [DeviceModalMatrixRowId.QUEUEING_ANNOUNCEMENT_GENERAL]: {
            rowId: DeviceModalMatrixRowId.QUEUEING_ANNOUNCEMENT_GENERAL,
            confluenceTitle: 'Queueing Announcement (General)',
            primaryDeviceTypeIds: [DeviceType.VoicemailAnnouncementGeneral],
            capabilities: STANDARD_NON_GROUP_FLAGS
        },
        [DeviceModalMatrixRowId.AUTO_ATTENDANT]: {
            rowId: DeviceModalMatrixRowId.AUTO_ATTENDANT,
            confluenceTitle: 'Auto Attendant',
            primaryDeviceTypeIds: [DeviceType.AutoAttendant],
            capabilities: STANDARD_NON_GROUP_FLAGS
        },
        [DeviceModalMatrixRowId.MUSIC_ON_HOLD]: {
            rowId: DeviceModalMatrixRowId.MUSIC_ON_HOLD,
            confluenceTitle: 'Music On Hold',
            primaryDeviceTypeIds: [DeviceType.MusicOnHold],
            capabilities: STANDARD_NON_GROUP_FLAGS
        },
        [DeviceModalMatrixRowId.CONFERENCE]: {
            rowId: DeviceModalMatrixRowId.CONFERENCE,
            confluenceTitle: 'Conference',
            primaryDeviceTypeIds: [DeviceType.Conference],
            capabilities: STANDARD_NON_GROUP_FLAGS
        },
        [DeviceModalMatrixRowId.PILOT_ALTERNATIVE]: {
            rowId: DeviceModalMatrixRowId.PILOT_ALTERNATIVE,
            confluenceTitle: 'Pilot/Alternative',
            primaryDeviceTypeIds: [DeviceType.PilotNumber],
            capabilities: STANDARD_NON_GROUP_FLAGS
        },
        [DeviceModalMatrixRowId.DNIS_ENTRY]: {
            rowId: DeviceModalMatrixRowId.DNIS_ENTRY,
            confluenceTitle: 'DNIS Entry',
            primaryDeviceTypeIds: [DeviceType.DnisEntry],
            capabilities: STANDARD_NON_GROUP_FLAGS
        },
        [DeviceModalMatrixRowId.DEPARTMENT]: {
            rowId: DeviceModalMatrixRowId.DEPARTMENT,
            confluenceTitle: 'Department',
            primaryDeviceTypeIds: [DeviceType.GroupDepartment],
            capabilities: STANDARD_GROUP_FLAGS_NO_DNIS
        },
        [DeviceModalMatrixRowId.SUPER_GROUP]: {
            rowId: DeviceModalMatrixRowId.SUPER_GROUP,
            confluenceTitle: 'Super Group',
            primaryDeviceTypeIds: [DeviceType.GroupSuper],
            capabilities: STANDARD_GROUP_FLAGS_NO_DNIS
        },
        [DeviceModalMatrixRowId.GROUP_COLLECTIVE]: {
            rowId: DeviceModalMatrixRowId.GROUP_COLLECTIVE,
            confluenceTitle: 'Group (Collective)',
            primaryDeviceTypeIds: [DeviceType.GroupCollective],
            capabilities: STANDARD_GROUP_FLAGS_NO_DNIS
        },
        [DeviceModalMatrixRowId.GROUP_LINEAR]: {
            rowId: DeviceModalMatrixRowId.GROUP_LINEAR,
            confluenceTitle: 'Group (Linear)',
            primaryDeviceTypeIds: [DeviceType.GroupLinear],
            capabilities: STANDARD_GROUP_FLAGS_NO_DNIS
        },
        [DeviceModalMatrixRowId.GROUP_CYCLIC]: {
            rowId: DeviceModalMatrixRowId.GROUP_CYCLIC,
            confluenceTitle: 'Group (Cyclic)',
            primaryDeviceTypeIds: [DeviceType.GroupCyclic],
            capabilities: STANDARD_GROUP_FLAGS_NO_DNIS
        },
        [DeviceModalMatrixRowId.GROUP_ACD]: {
            rowId: DeviceModalMatrixRowId.GROUP_ACD,
            confluenceTitle: 'Group ACD',
            primaryDeviceTypeIds: [DeviceType.GroupAcd],
            capabilities: GROUP_ACD_FLAGS
        },
        [DeviceModalMatrixRowId.CALL_QUEUE]: {
            rowId: DeviceModalMatrixRowId.CALL_QUEUE,
            confluenceTitle: 'Call Queue',
            primaryDeviceTypeIds: [DeviceType.GroupAcd],
            capabilities: CALL_QUEUE_FLAGS
        },
        [DeviceModalMatrixRowId.GROUP_TRUNK]: {
            rowId: DeviceModalMatrixRowId.GROUP_TRUNK,
            confluenceTitle: 'Group (Trunk)',
            primaryDeviceTypeIds: [DeviceType.GroupTrunk],
            capabilities: STANDARD_GROUP_FLAGS_NO_DNIS
        },
        [DeviceModalMatrixRowId.GROUP_VOICEMAIL]: {
            rowId: DeviceModalMatrixRowId.GROUP_VOICEMAIL,
            confluenceTitle: 'Group (VoiceMail)',
            primaryDeviceTypeIds: [DeviceType.GroupVoicemail],
            capabilities: STANDARD_GROUP_FLAGS_NO_DNIS
        },
        [DeviceModalMatrixRowId.GROUP_PAGE]: {
            rowId: DeviceModalMatrixRowId.GROUP_PAGE,
            confluenceTitle: 'Group (Page)',
            primaryDeviceTypeIds: [DeviceType.GroupPage],
            capabilities: STANDARD_GROUP_FLAGS_NO_DNIS
        }
    })

const MATRIX_ROW_IDS: readonly DeviceModalMatrixRowId[] = Object.freeze(
    Object.values(DeviceModalMatrixRowId) as DeviceModalMatrixRowId[]
)

/**
 * Returns every {@link DeviceModalMatrixRowId} in a stable order.
 *
 * @returns Readonly list of matrix row ids.
 */
export const getAllDeviceModalMatrixRowIds = (): readonly DeviceModalMatrixRowId[] => MATRIX_ROW_IDS

export type ResolveDeviceModalMatrixRowOptions = Readonly<{
    /**
     * When {@code true}, use {@link DeviceModalMatrixRowId.CALL_QUEUE} for device type
     * {@code GROUP_ACD} (43). When {@code false} or omitted, {@code GROUP_ACD} uses
     * {@link DeviceModalMatrixRowId.GROUP_ACD}.
     */
    isMsTeamsCallQueuePresentation?: boolean
}>

/**
 * Resolves the Confluence matrix row for a {@code CTelephonyModelConst} device type id.
 *
 * @param deviceTypeId - Numeric device type from the API / form.
 * @param options - Disambiguation for shared ids (e.g. Call Queue vs Group ACD).
 * @returns Matrix row definition, or {@code null} when the id is not part of the APM matrix.
 */
export const resolveDeviceModalMatrixRow = (
    deviceTypeId: number,
    options?: ResolveDeviceModalMatrixRowOptions
): OnlyNullable<DeviceModalMatrixRowDefinition> => {
    if (deviceTypeId === DeviceType.GroupAcd) {
        return options?.isMsTeamsCallQueuePresentation === true
            ? DEVICE_MODAL_CAPABILITY_MATRIX[DeviceModalMatrixRowId.CALL_QUEUE]
            : DEVICE_MODAL_CAPABILITY_MATRIX[DeviceModalMatrixRowId.GROUP_ACD]
    }

    const match: Definable<DeviceModalMatrixRowDefinition> = MATRIX_ROW_IDS.map(
        (id: DeviceModalMatrixRowId): DeviceModalMatrixRowDefinition => DEVICE_MODAL_CAPABILITY_MATRIX[id]
    ).find((row: DeviceModalMatrixRowDefinition): boolean => row.primaryDeviceTypeIds.includes(deviceTypeId))

    return match ?? null
}

/**
 * Returns capability flags for a device type, or {@code null} if unmapped.
 *
 * @param deviceTypeId - Numeric device type id.
 * @param options - Optional disambiguation for shared ids.
 * @returns Flags or {@code null}.
 */
export const getDeviceModalCapabilityFlagsForDeviceType = (
    deviceTypeId: number,
    options?: ResolveDeviceModalMatrixRowOptions
): OnlyNullable<DeviceModalCapabilityFlags> => {
    const row: OnlyNullable<DeviceModalMatrixRowDefinition> = resolveDeviceModalMatrixRow(deviceTypeId, options)

    return row?.capabilities ?? null
}
